import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectUserID, selectFullName } from "../../../redux/IchthusSlice";
import Loader from "../../loader/Loader";
import { FaSave } from "react-icons/fa";
import {
  Search,
  Loader2,
  Layers,
  Calculator,
  Scale,
  PackageCheck,
  Tag,
  ArrowLeft,
  TriangleAlert,
  ChevronDown,
  ChevronUp,
  History,
} from "lucide-react";
import { domain, domainAccounting, weighingScaleDomain } from "../../../security";
import { 
  ClipboardList, CheckCircle2, 
  Package, MessageSquare, Check, Circle,
  Edit, 
  PackageOpen, 
  X, 
  Info, 
  Calendar, 
  Plus,
} from 'lucide-react';
import Pagination from "../Pagination";
import { PortSelect1 } from "../Selections";

// ─── STYLE CONSTANTS ──────────────────────────────────────────────────────────
const labelClass =
  "mb-1 ml-0.5 block text-[10px] font-black uppercase tracking-widest text-gray-500";

const getColorClasses = (color) => {
  const map = {
    blue: {
      text: "text-blue-500",
      border: "border-blue-200",
      ring: "focus:ring-blue-200 focus:border-blue-400",
      bar: "bg-blue-400",
    },
    amber: {
      text: "text-amber-500",
      border: "border-amber-200",
      ring: "focus:ring-amber-200 focus:border-amber-400",
      bar: "bg-amber-400",
    },
    slate: {
      text: "text-slate-500",
      border: "border-slate-200",
      ring: "focus:ring-slate-200 focus:border-slate-400",
      bar: "bg-slate-400",
    },
    emerald: {
      text: "text-emerald-500",
      border: "border-emerald-200",
      ring: "focus:ring-emerald-200 focus:border-emerald-400",
      bar: "bg-emerald-400",
    },
    violet: {
      text: "text-violet-500",
      border: "border-violet-200",
      ring: "focus:ring-violet-200 focus:border-violet-400",
      bar: "bg-violet-400",
    },
    rose: {
      text: "text-rose-500",
      border: "border-rose-200",
      ring: "focus:ring-rose-200 focus:border-rose-400",
      bar: "bg-rose-400",
    },
    yellow: {
      text: "text-yellow-500",
      border: "border-yellow-200",
      ring: "focus:ring-yellow-200 focus:border-yellow-400",
      bar: "bg-yellow-400",
    },
    orange: {
      text: "text-orange-500",
      border: "border-orange-200",
      ring: "focus:ring-orange-200 focus:border-orange-400",
      bar: "bg-orange-400",
    },
    cyan: {
      text: "text-cyan-500",
      border: "border-cyan-200",
      ring: "focus:ring-cyan-200 focus:border-cyan-400",
      bar: "bg-cyan-400",
    },
  };
  return map[color] || map.slate;
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const fmt = (val) =>
  new Intl.NumberFormat("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val ?? 0);

// ─── HELPER COMPONENTS ────────────────────────────────────────────────────────
const WeightBar = ({ label, value, total, barColor }) => {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px] font-bold text-gray-500 uppercase tracking-wide">
        <span>{label}</span>
        <span>{pct.toFixed(1)}%</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const ActualMeasurements = ({ onClose }) => {
  const currentUserId = useSelector(selectUserID);
  const [searchPR, setSearchPR] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [itemsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  // Data States
  const [actualMeasurements, setActualMeasurements] = useState([]);
  const [filteredActualMeasurements, setFilteredActualMeasurements] = useState([]);
  const [production, setProduction] = useState(null);
  const [selectedDoItem, setSelectedDoItem] = useState(null);
  const [bomData, setBomData] = useState(null);
  const [targetMultiplier, setTargetMultiplier] = useState(1);
  const [hasExistingMeasurements, setHasExistingMeasurements] = useState(false);

  // Form / Dynamic Entry States
  const [taggingGroups, setTaggingGroups] = useState([]);
  const [entries, setEntries] = useState({});
  const [remarks, setRemarks] = useState("");
  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(false);
  // ─── INITIAL FETCH (Metadata) ─────────────────────────────────────────────
  useEffect(() => {
    const fetchTaggingGroups = async () => {
      try {
        const res = await axios.get(`${domain}/api/ProductionTaggings/grouped`);
        setTaggingGroups(res.data || []);
      } catch {
        toast.error("Failed to load tagging groups.");
      }
    };
    fetchTaggingGroups();
  }, []);

  
  const fetchData = useCallback(async () => {
    setIsSearching(true);

    try {
      const prodRes = await axios.get(`${domain}/api/ActualMeasurements`);

      setActualMeasurements(prodRes.data || [])
    } catch (error) {
      toast.error("Error fetching production details.");
      console.error(error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    const lowerSearch = searchPR.toLowerCase();
    const results = actualMeasurements.filter((item) =>
      (item.productionNumber || "").toLowerCase().includes(lowerSearch),
    );
    setFilteredActualMeasurements(results);
    setCurrentPage(1);
  }, [searchPR, actualMeasurements]);

  // ─── CLEAR FORM HELPER ──────────────────────────────────────────────────
  const resetForm = () => {
    setSearchPR("");
    setProduction(null);
    setSelectedDoItem(null);
    setBomData(null);
    setEntries({});
    setRemarks("");
    setTargetMultiplier(1);
    setHasExistingMeasurements(false);
  };

  // ─── SEARCH LOGIC ─────────────────────────────────────────────────────────
  const handleSearch = useCallback(async () => {
    if (!searchPR.trim()) {
      return toast.warning(
        "Please enter a Production Number (e.g., PR-00000000001)",
      );
    }

    setIsSearching(true);
    setProduction(null);
    setSelectedDoItem(null);
    setBomData(null);
    setEntries({});
    setHasExistingMeasurements(false);
    setRemarks("");

    try {
      const prodRes = await axios.get(`${domain}/api/Productions`);
      const foundProd = (prodRes.data || []).find(
        (p) =>
          p.productionNumber?.toLowerCase() === searchPR.toLowerCase().trim(),
      );

      setActualMeasurements(prodRes.data || [])

      if (!foundProd) {
        toast.error("Production Record not found.");
        setIsSearching(false);
        return;
      }

      const doRes = await axios.get(`${domain}/api/DeliveryOrders`);
      const foundDO = (doRes.data || []).find(
        (d) => d.id === foundProd.deliveryOrderId,
      );
      const foundItem = foundDO?.deliveryOrderItems?.find(
        (i) => i.id === foundProd.deliveryOrderItemId,
      );

      if (!foundItem) {
        toast.error("Linked Delivery Order Item not found.");
        setIsSearching(false);
        return;
      }

      const bomRes = await axios.get(
        `${domain}/api/ProductBom/active?bomProductId=${foundItem.productId}`,
      );

      setProduction(foundProd);
      setSelectedDoItem(foundItem);
      setBomData(bomRes.data);
      setTargetMultiplier(
        foundItem.quantityExpected ?? foundItem.quantity ?? 1,
      );

      setHasExistingMeasurements(foundProd.hasActualMeasurements === true);

      try {
        const existingMeasRes = await axios.get(
          `${domain}/api/ActualMeasurements/by-production/${foundProd.id}`,
        );
        if (existingMeasRes.data) {
          const prefilled = {};
          if (existingMeasRes.data.entries) {
            existingMeasRes.data.entries.forEach((e) => {
              if (prefilled[e.productionTaggingId]) {
                prefilled[e.productionTaggingId] =
                  parseFloat(prefilled[e.productionTaggingId]) +
                  parseFloat(e.quantity);
              } else {
                prefilled[e.productionTaggingId] = parseFloat(e.quantity);
              }
            });
          }
          setEntries(Object.keys(prefilled).length > 0 ? prefilled : {});
          setRemarks(existingMeasRes.data.remarks || "");
        }
      } catch {
        // No existing measurements — fine
      }

      toast.success("Production data loaded.");
    } catch (error) {
      toast.error("Error fetching production details.");
      console.error(error);
    } finally {
      setIsSearching(false);
    }
  }, [searchPR]);

  const [selectedData, setSelectedData] = useState(null);
  const selectHandleClick = useCallback(async (_search) => {
    if (!_search.trim()) {
      return toast.warning(
        "Please enter a Production Number (e.g., PR-00000000001)",
      );
    }

    setIsLoading(true);
    setProduction(null);
    setSelectedDoItem(null);
    setBomData(null);
    setEntries({});
    setHasExistingMeasurements(false);
    setRemarks("");

    try {
      const prodRes = await axios.get(`${domain}/api/Productions`);
      const foundProd = (prodRes.data || []).find(
        (p) =>
          p.productionNumber?.toLowerCase() === _search.toLowerCase().trim(),
      );

      // setActualMeasurements(prodRes.data || [])

      if (!foundProd) {
        toast.error("Production Record not found.");
        setIsSearching(false);
        return;
      }

      const doRes = await axios.get(`${domain}/api/DeliveryOrders`);
      const foundDO = (doRes.data || []).find(
        (d) => d.id === foundProd.deliveryOrderId,
      );
      const foundItem = foundDO?.deliveryOrderItems?.find(
        (i) => i.id === foundProd.deliveryOrderItemId,
      );

      if (!foundItem) {
        toast.error("Linked Delivery Order Item not found.");
        setIsSearching(false);
        return;
      }

      const bomRes = await axios.get(
        `${domain}/api/ProductBom/active?bomProductId=${foundItem.productId}`,
      );

      setProduction(foundProd);
      setSelectedDoItem(foundItem);
      setBomData(bomRes.data);
      setTargetMultiplier(
        foundItem.quantityExpected ?? foundItem.quantity ?? 1,
      );

      setHasExistingMeasurements(foundProd.hasActualMeasurements === true);

      try {
        const existingMeasRes = await axios.get(
          `${domain}/api/ActualMeasurements/by-production/${foundProd.id}`,
        );
        if (existingMeasRes.data) {
          setSelectedData(existingMeasRes.data)
          const prefilled = {};
          if (existingMeasRes.data.entries) {
            existingMeasRes.data.entries.forEach((e) => {
              if (prefilled[e.productionTaggingId]) {
                prefilled[e.productionTaggingId] =
                  parseFloat(prefilled[e.productionTaggingId]) +
                  parseFloat(e.quantity);
              } else {
                prefilled[e.productionTaggingId] = parseFloat(e.quantity);
              }
            });
          }
          setEntries(Object.keys(prefilled).length > 0 ? prefilled : {});
          setRemarks(existingMeasRes.data.remarks || "");
        }
      } catch {
        // No existing measurements — fine
      }

      toast.success("Production data loaded.");
    } catch (error) {
      toast.error("Error fetching production details.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /* const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  }; */

  // ─── TOTAL ACTUAL WEIGHT ──────────────────────────────────────────────────
  const totalActualWeight = useMemo(() => {
    let total = 0;
    taggingGroups.forEach((group) =>
      group.tags.forEach((tag) => {
        if (
          tag.unit?.toLowerCase() === "kg" ||
          tag.unit?.toLowerCase() === "g"
        ) {
          total += parseFloat(entries[tag.id] || 0);
        }
      }),
    );
    return total;
  }, [entries, taggingGroups]);

  const handleEntryChange = (tagId, value) => {
    if (value !== "" && (isNaN(value) || parseFloat(value) < 0)) return;
    setEntries((prev) => ({
      ...prev,
      [tagId]: value === "" ? "" : parseFloat(value),
    }));
  };

  const singlePushToAccounting = async (_data) => {

    // const finalData = filtered?.filter(i => i.receiptDate >= dateFrom && i.receiptDate <= dateTo).map(gr => {
    const finalData = {
      productionDate: _data?.productionDate ?? null,
      productionId: _data?.productionId ?? null,
      itemCode: _data?.itemCode ?? null,
      customerId: _data?.customerId ?? null,
      totalVolume: _data?.totalVolume ?? 0,
      productionDetails: _data?.productionDetails ?? [],
    }

    try {

      await axios.post(
        `${domainAccounting}/api/Production/bulk`,
        [finalData],
      );

      toast.success(
        `Successfully pushed 1 to Accounting!`,
      );

    } catch (error) {
      console.error(error);
      toast.error(`Failed to push "order" to Accounting.`);
    }
  };

  // ─── SAVE LOGIC ────────────────────────────────────────────────────────
  const handleSave = async (_data) => {
    /* if (totalActualWeight <= 0) {
      return toast.warning("Please enter at least one measurement.");
    } */

    const formattedEntries = [];

    for (const item of _data?.entries) {
      formattedEntries.push({
        productionTaggingId: item.productId,
        productId: item.productId || null,
        uomId: item?.uomId || null,
        quantity: item?.quantity || 0,
        moistureCorrected: item?.moistureCorrected || 0,
      });
    }

    if (formattedEntries.length === 0) {
      return toast.error("Please enter at least one material quantity.");
    }

    setIsSaving(true);

    const payload = {
      userID: currentUserId,
      productionId: production.id,
      totalActualWeight: _data?.quantityReceived ?? 0,
      remarks: _data?.remarks ?? "",
      entries: formattedEntries,
    };

    try {
      const response = await axios.post(`${domain}/api/ActualMeasurements`, payload);
      toast.success("Actual measurements saved successfully!");

      const output = response.data?.output;
      if(output){
        await singlePushToAccounting(output)
      }

      await fetchData()

      // ✨ NEW LOGIC: If embedded in a modal with an onClose, close it.
      // Otherwise, completely reset the page so it's fresh for the next entry.
      if (onClose) {
        onClose();
      } else {
        resetForm();
      }
    } catch (error) {
      console.error('There was a problem adding data:', error);
      toast.error(
        error.response?.data?.message || "Failed to save measurements.",
      );
    } finally {
      setIsSaving(false);
      closeAllForms();
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredActualMeasurements.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const [selectedProduction, setSelectedProduction] = useState(null);
  const [showSelectionForm, setShowSelectionForm] = useState(false);
  const openSelectionForm = () => {
    setShowSelectionForm(true)
  }
  const closeSelectionForm = () => {
    setShowSelectionForm(false)
    setSelectedProduction(null)
  }

  const [showAddForm, setShowAddForm] = useState(false);
  const openAddForm = () => {
    setShowAddForm(true)
  }
  const closeAddForm = () => {
    setShowAddForm(false)
  }

  const addHandleClick = async (_search) => {
    if(!selectedProduction){
      alert("Please select a production")
      return;
    }

    await selectHandleClick(_search)

    openAddForm();
  }

  const [showUpdateForm, setShowUpdateForm] = useState(false)
  const openUpdateForm = () => {
    setShowUpdateForm(true)
  }
  const closeUpdateForm = () => {
    setShowUpdateForm(false)
    setProduction(null)
    setSelectedData(null)
  }

  const updateHandleClick = async (_search) => {
    await selectHandleClick(_search)

    openUpdateForm();
  }

  const [showViewForm, setShowViewForm] = useState(false)
  const openViewForm = () => {
    setShowViewForm(true)
  }
  const closeViewForm = () => {
    setShowViewForm(false)
    setProduction(null)
    setSelectedData(null)
  }

  const viewHandleClick = async (_search) => {
    await selectHandleClick(_search)

    openViewForm();
  }

  const closeAllForms = () => {
    closeSelectionForm()
    closeAddForm()
    closeUpdateForm()
    closeViewForm()
  }

  const [showSyncToAccountingForm, setShowSyncToAccountingForm] = useState(false);
  const openSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(true);
  }
  const closeSyncToAccountingForm = () => {
    setShowSyncToAccountingForm(false);
  }

  const handleSyncToAccounting = async (dateFrom, dateTo) => {
    setIsLoading(true)

    const getDateOnly = value => {
      const d = new Date(value);
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    };

    const filtered = actualMeasurements.filter(i => {
      const date = getDateOnly(i.productionDate);
      const from = getDateOnly(dateFrom);
      const to = getDateOnly(dateTo);

      return date >= from && date <= to;
    });

    // const finalData = filtered?.filter(i => i.receiptDate >= dateFrom && i.receiptDate <= dateTo).map(gr => {
    const finalData = filtered?.map(production => {

      return {
        productionDate: production?.productionDate ?? null,
        productionId: production?.productionId ?? null,
        itemCode: production?.itemCode ?? null,
        customerId: production?.customerCode ?? null,
        totalVolume: production?.totalActualWeight ?? null,
        productionDetails: production?.entries?.map((entry) => {
          return {
            itemCode: entry?.itemCode ?? null,
            uomName: entry?.uomCode ?? null,
            planned: entry?.planned ?? null,
            actualUsed: entry?.quantity ?? null,
            moistureCorrected: entry?.moistureCorrected ?? null,
          }
        }),
      }
    });

    try {

      await axios.post(
        `${domainAccounting}/api/Production/bulk`,
        finalData,
      );

      toast.success(
        `Successfully pushed ${filtered?.length || 0 } to Accounting!`,
      );

    } catch (error) {
      console.error(error);
      toast.error(`Failed to push "order"} to Accounting.`);
    } finally {
      setIsLoading(false)
    }
  };


  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <>
      <div className="w-full flex-col bg-transparent pb-8">
        <ToastContainer position="top-right" autoClose={3000} />

        <div className="w-full mx-auto flex flex-col flex-grow rounded-2xl bg-white shadow-sm border border-gray-200 overflow-hidden">
          {/* HEADER */}
          <div className="flex items-center justify-between border-b border-gray-100 bg-white px-6 py-5 shrink-0">
            <div className="flex items-center gap-4">
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-2 mr-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors border border-transparent hover:border-indigo-100"
                  title="Go Back"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-lg shadow-indigo-200">
                <Scale size={22} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 leading-none">
                  Actual Measurements
                </h2>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1.5">
                  Record Final Data
                </p>
              </div>
            </div>
          </div>

          {/* SEARCH BAR STRIP */}
          <div className="bg-slate-50 border-b border-slate-100 p-5 shrink-0 flex flex-col md:flex-row items-start justify-between gap-2">
            <div className="w-full max-w-2xl relative flex items-center">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Scan or type Production Number (e.g., PR-00000000001)"
                value={searchPR}
                onChange={(e) => setSearchPR(e.target.value)}
                // onKeyDown={handleKeyDown}
                className="w-full pl-12 pr-32 py-3.5 bg-white border border-indigo-200 rounded-xl shadow-sm text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 uppercase placeholder:normal-case placeholder:font-medium transition-all"
                autoFocus
              />
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-xs font-bold transition-all disabled:opacity-50 hidden items-center gap-2"
              >
                {isSearching ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  "Search"
                )}
              </button>
            </div>

            <div className="flex flex-row gap-2">
              <button
                onClick={() => openSyncToAccountingForm()}
                className="flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl text-sm font-medium transition"
              >
                <History size={16} />
                <span className="hidden sm:inline">Sync To Accounting</span>
              </button>
              
              <button
                onClick={openSelectionForm}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-5 py-2.5 rounded-xl shadow-md shadow-indigo-200 transition-all"
              >
                <Plus size={18} />
                <span className="md:inline">Add New</span>
              </button>
            </div>
          </div>

          {/* BODY */}
          <div className="flex-grow overflow-y-auto bg-gray-50/50 p-6">

            <div>

              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-200">
                        <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          Production No.
                        </th>
                        <th className="py-4 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">
                          Products
                        </th>
                        <th className="py-4 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">
                          Produced
                        </th>
                        <th className="py-4 px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    
                    <tbody className="divide-y divide-slate-100">
                      {actualMeasurements?.length === 0 ? (
                        /* Enhanced Empty State */
                        <tr>
                          <td colSpan={4} className="py-16">
                            <div className="flex flex-col items-center justify-center text-slate-400">
                              <div className="bg-slate-50 p-4 rounded-full mb-3">
                                <PackageOpen size={32} className="text-slate-300" />
                              </div>
                              <p className="text-sm font-semibold text-slate-500">No actual measurements recorded</p>
                              <p className="text-[10px] uppercase tracking-tight">Records will appear here once production starts</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        currentItems?.map((item, index) => (
                          <tr key={index} className="group hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => {viewHandleClick(item.productionNumber || "");}}>
                            {/* Production No */}
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-3">
                                <div className="bg-indigo-50 p-2 rounded-lg text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                                  <ClipboardList size={14} />
                                </div>
                                <span className="font-mono text-xs font-bold text-slate-700">
                                  {item.productionNumber}
                                </span>
                              </div>
                            </td>

                            {/* Product Name */}
                            <td className="py-4 px-4 text-center">
                              <span className="text-xs font-black text-slate-800 uppercase tracking-tight">
                                {item.productName}
                              </span>
                            </td>

                            {/* Produced Quantity */}
                            <td className="py-4 px-4 text-center">
                              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-100 rounded-full">
                                <span className="text-xs font-black text-emerald-700">
                                  {item.totalActualWeight}
                                </span>
                                <span className="text-[10px] font-bold text-emerald-500 uppercase">
                                  {item.uomName || "PCS"}
                                </span>
                              </div>
                            </td>

                            {/* Actions */}
                            <td className="py-4 px-6 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateHandleClick(item.productionNumber || "");
                                  }}
                                  className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold text-slate-500 bg-white border border-slate-200 rounded-lg hover:border-indigo-500 hover:text-indigo-600 hover:shadow-sm transition-all uppercase"
                                >
                                  <Edit size={12} />
                                  Edit Details
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 md:block hidden">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredActualMeasurements.length}
                  currentPage={currentPage}
                  paginate={paginate}
                />
              </div>

              <div className="hidden grid-cols-1 xl:grid-cols-2 gap-8 h-full items-start">
                {/* LEFT COLUMN: Target Ingredients (BOM) */}
                <div className="flex flex-col max-h-full rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden sticky top-0">
                  <div className="p-4 border-b border-gray-100 bg-indigo-50/50 flex items-center gap-2">
                    <Layers size={16} className="text-indigo-500" />
                    <h3 className="text-xs font-black uppercase tracking-widest text-indigo-800">
                      Target Ingredients (BOM Multiplier)
                    </h3>
                  </div>

                  <div className="p-4 flex-grow overflow-y-auto space-y-4 bg-white">
                    <div className="flex items-center justify-between p-4 bg-indigo-600 rounded-xl text-white shadow-md shadow-indigo-200">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-white/20 rounded-lg backdrop-blur-sm">
                          <PackageCheck size={18} className="text-white" />
                        </div>
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-indigo-200 mb-0.5">
                            Target Product
                          </p>
                          <p className="text-base font-bold leading-tight">
                            {selectedDoItem?.productName || "Unknown"}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-black leading-none">
                          {targetMultiplier}
                        </p>
                        <p className="text-[10px] font-black uppercase tracking-widest text-indigo-200 mt-0.5">
                          Orders to Produce
                        </p>
                      </div>
                    </div>

                    <div className="border border-indigo-100 rounded-xl overflow-hidden shadow-sm">
                      <div className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 px-4 py-2.5 bg-indigo-50/50 border-b border-indigo-100 text-[10px] font-black text-indigo-500 uppercase tracking-wider">
                        <div>Ingredient</div>
                        <div className="w-[60px] text-right">Base</div>
                        <div className="w-[50px] text-center">Multi</div>
                        <div className="w-[10px]"></div>
                        <div className="w-[85px] text-right">Target</div>
                      </div>
                      <div className="divide-y divide-gray-50">
                        {bomData?.components?.map((comp) => {
                          const targetQty = comp.quantity * targetMultiplier;
                          return (
                            <div
                              key={comp.id}
                              className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                            >
                              <div className="flex flex-col overflow-hidden">
                                <span className="text-sm font-bold text-gray-800 truncate">
                                  {comp.componentProductName}
                                </span>
                                <span className="text-[9px] font-bold text-gray-400 tracking-wider uppercase mt-0.5">
                                  {comp.componentItemCode || "N/A"}
                                </span>
                              </div>
                              <div className="w-[60px] text-right">
                                <p className="text-xs font-semibold text-gray-500">
                                  {fmt(comp.quantity)}
                                </p>
                              </div>
                              <div className="w-[50px] text-center">
                                <span className="text-[10px] font-black text-indigo-300">
                                  × {targetMultiplier}
                                </span>
                              </div>
                              <div className="w-[10px] text-center">
                                <span className="text-xs font-black text-gray-300">
                                  =
                                </span>
                              </div>
                              <div className="w-[85px] text-right">
                                <p className="text-sm font-black text-indigo-600">
                                  {fmt(targetQty)}
                                </p>
                                <p className="text-[9px] text-indigo-400 font-bold uppercase">
                                  {comp.uomName || "Units"}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN: Simple Data Entry — No Stock Checks */}
                <div
                  className={`space-y-5 transition-opacity duration-300 ${!selectedDoItem ? "opacity-40 pointer-events-none grayscale-[50%]" : "opacity-100"}`}
                >
                  {/* WARNING BANNER IF ALREADY FILLED */}
                  {hasExistingMeasurements && (
                    <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <TriangleAlert
                        size={20}
                        className="text-amber-500 shrink-0 mt-0.5"
                      />
                      <div>
                        <h4 className="text-sm font-bold text-amber-800">
                          Measurements Already Saved
                        </h4>
                        <p className="text-xs text-amber-700 mt-1">
                          This production already has recorded measurements.
                          Saving again will replace the previous data.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Simple Tagging Groups — Pure Data Entry */}
                  {taggingGroups.map((group) => (
                    <div
                      key={group.groupName}
                      className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-4"
                    >
                      <h3 className="text-xs font-black uppercase tracking-widest text-gray-800 flex items-center gap-2">
                        <Layers size={14} className="text-indigo-400" />{" "}
                        {group.groupName}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {group.tags.map((tag) => {
                          const val = entries[tag.id] ?? "";
                          const c = getColorClasses(tag.color);

                          return (
                            <div key={tag.id} className="space-y-1.5">
                              <label className={labelClass}>{tag.tagName}</label>
                              <div className="relative group">
                                <Tag
                                  size={15}
                                  className={`absolute left-3 top-1/2 -translate-y-1/2 ${c.text}`}
                                />
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={val}
                                  onChange={(e) =>
                                    handleEntryChange(tag.id, e.target.value)
                                  }
                                  placeholder="0.00"
                                  className={`block w-full rounded-xl pl-9 pr-10 py-2.5 text-sm font-bold text-gray-900 outline-none transition-all bg-gray-50 focus:bg-white border ${c.border} ${c.ring}`}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-gray-400 uppercase">
                                  {tag.unit}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Mix Composition Breakdown */}
                  {totalActualWeight > 0 && (
                    <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden transition-all">
                      <button
                        onClick={() =>
                          setIsBreakdownExpanded(!isBreakdownExpanded)
                        }
                        className="w-full flex items-center justify-between p-5 hover:bg-gray-200/50 transition-colors focus:outline-none"
                      >
                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                          Mix Composition Breakdown
                        </p>
                        {isBreakdownExpanded ? (
                          <ChevronUp size={16} className="text-gray-400" />
                        ) : (
                          <ChevronDown size={16} className="text-gray-400" />
                        )}
                      </button>

                      {isBreakdownExpanded && (
                        <div className="px-5 pb-5 pt-1 space-y-3 border-t border-gray-100/50">
                          {taggingGroups
                            .flatMap((g) => g.tags)
                            .filter((t) => (parseFloat(entries[t.id]) || 0) > 0)
                            .map((tag) => (
                              <WeightBar
                                key={tag.id}
                                label={tag.tagName}
                                value={parseFloat(entries[tag.id]) || 0}
                                total={totalActualWeight}
                                barColor={getColorClasses(tag.color).bar}
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Total Actual Weight */}
                  <div
                    className={`rounded-xl border-2 p-5 shadow-sm transition-all duration-500 ${totalActualWeight > 0 ? "border-emerald-300 bg-emerald-50 scale-[1.01]" : "border-gray-200 bg-white"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-3 rounded-xl transition-colors ${totalActualWeight > 0 ? "bg-emerald-100" : "bg-gray-100"}`}
                        >
                          <Calculator
                            size={20}
                            className={
                              totalActualWeight > 0
                                ? "text-emerald-600"
                                : "text-gray-400"
                            }
                          />
                        </div>
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                            Total Actual
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            Sum of all entries
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p
                          className={`text-3xl font-black tabular-nums transition-all ${totalActualWeight > 0 ? "text-emerald-700" : "text-gray-300"}`}
                        >
                          {fmt(totalActualWeight)}
                        </p>
                        <p
                          className={`text-xs font-bold ${totalActualWeight > 0 ? "text-emerald-500" : "text-gray-300"}`}
                        >
                          {/* kilograms */}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Remarks */}
                  <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                    <label className={labelClass}>Remarks / Notes</label>
                    <textarea
                      rows={3}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      className="block w-full rounded-xl border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 transition-all focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 outline-none"
                      placeholder="Optional notes about this measurement..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="hidden items-center justify-end gap-3 border-t border-gray-100 bg-white px-6 py-4 shrink-0">
            {onClose && (
              <button
                onClick={onClose}
                className="text-sm font-bold text-gray-400 hover:text-gray-600 transition-colors px-4 py-2"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isSaving || !production}
              className="flex items-center gap-2 rounded-xl px-10 py-3.5 text-sm font-black text-white bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-200 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <FaSave size={16} />
              )}
              {isSaving
                ? "Saving..."
                : hasExistingMeasurements
                  ? "Overwrite Measurements"
                  : "Save Measurements"}
            </button>
          </div>
        </div>
      </div>

      { showSelectionForm &&
        <ProductionSelect onClose={closeSelectionForm} 
          selectedData={selectedProduction} setSelectedData={setSelectedProduction}
          addHandleClick={addHandleClick}
        />
      }

      { showAddForm &&
        <ProductionRecordModal production={production} onClose={closeAddForm} 
          onSubmit={handleSave}
          isEditMode={true}
        />
      }

      { showUpdateForm &&
        <ProductionRecordModal selectedData={selectedData} production={production} onClose={closeUpdateForm} 
          onSubmit={handleSave}
          isEditMode={true}
        />
      }

      { showViewForm &&
        <ProductionRecordModal selectedData={selectedData} production={production} onClose={closeViewForm} 
          onSubmit={handleSave}
          isEditMode={false}
        />
      }

      <SyncToAccountingModal 
        isOpen={showSyncToAccountingForm}
        onClose={closeSyncToAccountingForm}
        onConfirm={handleSyncToAccounting}
      />

      <div className="flex-1 w-full flex flex-col">
        { isLoading &&
          <div className="flex-1 flex items-center justify-center py-6">
            <Loader />
          </div>
        }
      </div>
    </>
  );
};

const ProductionRecordModal = ({ selectedData, production, onClose, onSubmit, isEditMode }) => {
  // --- STATES ---
  const fullName = useSelector(selectFullName);
  const [entries, setEntries] = useState([]);
  const [actualOutput, setActualOutput] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [quantityReceived, setQuantityReceived] = useState(0);
  const [checklist, setChecklist] = useState({
    Appearance: false, Packaging: false, Label: false, Dimension: false
  });

  useEffect(() => {
    if(selectedData){
      setActualOutput(selectedData?.totalActualWeight ?? 0);
      setQuantityReceived(selectedData?.totalActualWeight ?? 0);
      setRemarks(selectedData?.remarks ?? "");
      const _entries = selectedData?.entries?.map((item) => ({
        productId: item.productId ?? 0,
        quantity: item.quantity ?? 0,
        moistureCorrected: item.moistureCorrected ?? 0,
        uomId: item.uomId ?? 0,
      }))

      setEntries(_entries)
    } else {
      if(production){
        const _entries = production.entries?.map((item) => ({
          productId: item.productId ?? 0,
          quantity: item.quantity ?? 0,
          moistureCorrected: item.moistureCorrected ?? 0,
          uomId: item.uomId ?? 0,
        }))

        setEntries(_entries)
      }
    }
  }, [selectedData, production])

  /* useEffect(() => {
    if(production){
      const _entries = production.entries?.map((item) => ({
        productId: item.productId ?? 0,
        quantity: item.quantity ?? 0,
        uomId: item.uomId ?? 0,
      }))

      setEntries(_entries)
    }
  }, [production]) */

  useEffect(()=> {
    setQuantityReceived(actualOutput)
  }, [actualOutput])

  // --- HANDLERS ---
  const handleInputQuantityChange = (productId, value, uomId) => {
    const numericValue = value === "" ? 0 : parseFloat(value);
    setEntries(prev => {
      const idx = prev.findIndex(e => e.productId === productId);
      if (idx > -1) {
        const newArr = [...prev];
        newArr[idx] = { ...newArr[idx], quantity: numericValue };
        return newArr;
      }
      return [...prev, { productId, quantity: numericValue, uomId }];
    });
  };

  const handleInputMoistureCorrectedChange = (productId, value, uomId) => {
    const numericValue = value === "" ? 0 : parseFloat(value);
    setEntries(prev => {
      const idx = prev.findIndex(e => e.productId === productId);
      if (idx > -1) {
        const newArr = [...prev];
        newArr[idx] = { ...newArr[idx], moistureCorrected: numericValue };
        return newArr;
      }
      return [...prev, { productId, moistureCorrected: numericValue, uomId }];
    });
  };

  const toggleCheck = (item) => setChecklist(p => ({ ...p, [item]: !p[item] }));
  const passAll = () => setChecklist({ Appearance: true, Packaging: true, Label: true, Dimension: true });
  const resetChecklist = () => setChecklist({ Appearance: false, Packaging: false, Label: false, Dimension: false });

  useEffect(() => {
    if(selectedData){
      passAll();
    }
  }, [selectedData])

  const handleSubmit = () => {
    if(!quantityReceived || !actualOutput){
      alert("Please fill up required fields.")
      return;
    }

    if (!Object.values(checklist).every(value => value === true)) {
      alert("Update Quality Checklist part.");
      return;
    }

    if(entries?.length <= 0 || entries.find(i => i.quantity <= 0)){
      alert("Update the Ingredient Consumption Summary per Product.");
      return;
    }

    const _data = {
      entries: entries,
      actualOutput: actualOutput,
      quantityReceived: quantityReceived,
      remarks: remarks
    }
    onSubmit(_data)
  }

  useEffect(() => {
    if (production?.entries && entries) {

      // 1. Calculate how many units each ingredient allows
      const yieldPerIngredient = production.entries.map((requiredItem) => {
        // Find the user's input (actual quantity) for this specific product
        const actualEntry = entries.find((e) => e.productId === requiredItem.productId);

        if (!actualEntry || !actualEntry.quantity || actualEntry.quantity <= 0) {
          return 0; // If an ingredient is missing or 0, we can't produce anything
        }

        const requiredQty = requiredItem.componentQuantity || 0;
        const actualQty = actualEntry.quantity || 0;

        if (requiredQty === 0) return Infinity; // Safety: avoid division by zero

        // Calculate ratio: Available / Required (e.g., 5 liters available / 1 liter required = 5 units)
        return Math.floor(actualQty / requiredQty);
      });

      // 2. The limiting factor is the smallest number in the array
      const maxProduction = Math.min(...yieldPerIngredient);

      // 3. Set the state
      // Use isFinite to handle cases where there might be no entries
      setActualOutput(isFinite(maxProduction) ? maxProduction : 0);
    }
  }, [production, entries]); // Ensure dependencies are included

  const [portsByProduct, setPortsByProduct] = useState({});
  const [scaleWeights, setScaleWeights] = useState({});

  const fetchScaleWeights = useCallback(async () => {
    try {
      const response = await axios.get(
        `${weighingScaleDomain}/api/Scale/weights`,
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      setScaleWeights(response.data || {});
    } catch (error) {
      console.error("Error fetching scale weights:", error);
    }
  }, []);

  useEffect(() => {
    fetchScaleWeights();

    const interval = setInterval(() => {
      fetchScaleWeights();
    }, 500);

    return () => clearInterval(interval);
  }, [fetchScaleWeights]);

  const handlePortChange = (productId, port) => {
    setPortsByProduct(prev => ({
      ...prev,
      [productId]: port
    }));
  };

  const handleConnectScale = (productId) => {
    const selectedPort = portsByProduct[productId];

    if (!selectedPort) {
      return;
    }

    console.log(
      `Connecting product ${productId} to ${selectedPort}`
    );

    // Your scale connection logic here
  };



  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl my-auto overflow-hidden border border-slate-200">
        
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-600 p-2 rounded-lg shadow-lg shadow-indigo-100">
              <ClipboardList className="text-white w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-800 uppercase tracking-tight">Record Final Production Data</h1>
              <p className="text-xs text-slate-400 font-medium">Finalize measurements and quality checks for this batch.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400">
            <X size={20} />
          </button>
        </div>

        {/* Top Info Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-6 bg-slate-50/50 border-b border-slate-100">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-[10px] font-black text-slate-400 uppercase tracking-wider">
              {<ClipboardList size={12}/>} <span>{"Production Record"}</span>
            </div>
            <p className="text-sm font-black text-slate-800">{production?.productionNumber ?? "PR-0000"}</p>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-[10px] font-black text-slate-400 uppercase tracking-wider">
              {<Package size={12}/>} <span>{"Target Product"}</span>
            </div>
            <p className="text-sm font-black text-slate-800">{production?.productName ?? "Combo A"}</p>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-[10px] font-black text-slate-400 uppercase tracking-wider">
              {<Info size={12}/>} <span>{"Target Quantity"}</span>
            </div>
            <p className="text-sm font-black text-slate-800">{`${production?.totalActualWeight ?? 0} ${production?.uomName}`}</p>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-[10px] font-black text-slate-400 uppercase tracking-wider">
              {<Calendar size={12}/>} <span>{"Production Date"}</span>
            </div>
            <p className="text-sm font-black text-slate-800">{production?.productionDate ? formatDate(production.productionDate) : "N/A"}</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
          
          {/* LEFT COLUMN */}
          <div className="space-y-6">
            {/* Ingredient Summary */}
            <Section title="Ingredient Consumption" icon={<ClipboardList className="w-4 h-4 text-indigo-500" />}>
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-100">
                    <th className="py-3 font-bold">INGREDIENT</th>
                    <th className="py-3 font-bold text-center">UOM</th>
                    <th className="py-3 font-bold text-center">PLANNED</th>
                    <th className="py-3 font-bold text-center leading-tight">
                      MOISTURE <br /> CORRECTED
                    </th>
                    <th className="py-3 font-bold text-center leading-tight">
                      ACTUAL <br /> USED
                    </th>
                    {/* <th className="py-3 font-bold text-right">VARIANCE</th> */}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {production?.entries?.map((item) => {
                    const currentEntry = entries.find(e => e.productId === item.productId);
                    const planned = item.quantity ?? 0;
                    const actual = currentEntry ? parseFloat(currentEntry.quantity) || 0 : 0;
                    const _moistureCorrected = currentEntry ? parseFloat(currentEntry.moistureCorrected) || 0 : 0;
                    const varianceActual = actual - planned;
                    const varianceMoistureCorrected = _moistureCorrected - planned;

                    const selectedPort = portsByProduct[item.productId];

                    const weight = selectedPort
                      ? scaleWeights[selectedPort]
                      : "0.000 kg";
                    return (
                      <tr className="text-slate-600" key={item.productId}>
                        <td className="py-3 align-top">
                          <p className="font-bold">{item.productName}</p>
                          <p className="text-[10px] text-slate-400 font-mono">{`${item.brandName ?? ""}`}</p>

                          <div className="">
                            <PortSelect1
                              data={portsByProduct[item.productId] || ""}
                              setData={(value) => handlePortChange(item.productId, value)}
                              isEditable={true}
                              onConnect={() => handleConnectScale(item.productId)}
                            />

                            <span className="text-xs font-bold text-indigo-600">
                              {weight}
                            </span>
                          </div>

                        </td>
                        <td className="py-3 text-center align-top font-medium">{`${item.uomName}`}</td>
                        <td className="py-3 text-center align-top font-medium">{`${planned.toFixed(2)}`}</td>
                        <td className="py-3 text-center align-top">
                          <div className="flex flex-col items-center justify-start">
                            {isEditMode ? 
                              <input
                                type="number"
                                placeholder="0.00"
                                value={currentEntry?.moistureCorrected || ""}
                                onChange={(e) => handleInputMoistureCorrectedChange(item.productId, e.target.value, item.uomId)}
                                className="w-16 px-1 py-1 text-center border border-slate-200 rounded font-bold focus:ring-2 focus:ring-indigo-100 outline-none"
                              />
                              :
                              <span className="text-lg text-slate-400 font-bold">{currentEntry?.moistureCorrected || "0"}</span>
                            }
                            <span className="hidden text-[10px] text-slate-400 font-bold">{item.uomName}</span>
                            <span className={`hidden text-xs font-black ${varianceMoistureCorrected > 0 ? "text-rose-500" : "text-emerald-600"}`}>
                              {varianceMoistureCorrected > 0 ? `+${varianceMoistureCorrected.toFixed(2)}` : varianceMoistureCorrected.toFixed(2)} {item.uomName}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 text-center align-top">
                          <div className="flex flex-col items-center justify-start">
                            {isEditMode ? 
                              <input
                                type="number"
                                placeholder="0.00"
                                value={currentEntry?.quantity || ""}
                                onChange={(e) => handleInputQuantityChange(item.productId, e.target.value, item.uomId)}
                                className="w-16 px-1 py-1 text-center border border-slate-200 rounded font-bold focus:ring-2 focus:ring-indigo-100 outline-none"
                              />
                              :
                              <span className="text-lg text-slate-400 font-bold">{currentEntry?.quantity || "0"}</span>
                            }
                            <span className={`text-xs font-black ${varianceActual > 0 ? "text-rose-500" : "text-emerald-600"}`}>
                              {varianceActual > 0 ? `+${varianceActual.toFixed(2)}` : varianceActual.toFixed(2)} {item.uomName}
                            </span>

                            
                          </div>
                        </td>


                        {/* <td className={`hidden py-3 text-right font-black ${varianceActual > 0 ? "text-rose-500" : "text-emerald-600"}`}>
                          {varianceActual > 0 ? `+${varianceActual.toFixed(2)}` : varianceActual.toFixed(2)} {item.uomName}
                        </td> */}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Section>

            {/* Production Yield */}
            <Section title="Production Yield" icon={<CheckCircle2 className="w-4 h-4 text-emerald-500" />}>
              {(() => {
                const target = production?.totalActualWeight || 1;
                const actual = parseFloat(actualOutput) || 0;
                const yieldPerc = target > 0 ? (actual / target) * 100 : 0;
                return (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center">
                      <p className="text-[10px] font-black text-slate-400 mb-1 uppercase tracking-wider">{"Target Output"}</p>
                      <p className="text-xl font-black text-slate-800">{target.toFixed(2)}</p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">{production?.uomName ?? ""}</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border-2 border-slate-100 text-center shadow-sm">
                      <p className="text-[10px] font-black text-slate-400 mb-1 uppercase tracking-wider">Actual Output <span className="text-rose-500">*</span></p>
                      {isEditMode ? 
                        <input
                          type="number"
                          value={actualOutput || ""}
                          onChange={(e) => setActualOutput(e.target.value)}
                          placeholder="0.00"
                          className="w-full text-center bg-slate-50 border-2 border-slate-200 rounded-lg text-xl font-black text-slate-800 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 outline-none transition-all"
                        />
                      :
                        <p className="text-xl font-bold text-slate-400 mt-1">{actualOutput || "0"}</p>
                      }
                      <p className="text-[10px] font-bold text-slate-400 mt-1">{production?.uomName ?? ""}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100 flex flex-col items-center justify-center text-center">
                      <p className="text-[10px] font-black text-emerald-600 mb-1 uppercase tracking-wider">Calculated Yield</p>
                      <p className="text-2xl font-black text-emerald-600">{yieldPerc.toFixed(2)}%</p>
                    </div>
                  </div>
                );
              })()}
            </Section>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-6">
            
            {/* Checklist */}
            <Section title="Quality Checklist" icon={<CheckCircle2 className="w-4 h-4 text-indigo-500" />}>
              {isEditMode &&
                <div className="flex justify-end gap-3 mb-4 pb-2 border-b border-slate-100">
                  <button onClick={resetChecklist} className="text-[10px] font-bold text-slate-400 hover:text-rose-500 uppercase">Reset</button>
                  <div className="w-[1px] h-3 bg-slate-200 self-center"></div>
                  <button onClick={passAll} className="text-[10px] font-bold text-emerald-600 hover:underline uppercase">Pass All</button>
                </div>
              }
              <div className="space-y-2">
                {['Appearance', 'Packaging', 'Label', 'Dimension'].map((item) => {
                  const passed = checklist[item];
                  return (
                    <div key={item} className="flex justify-between items-center py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        {passed ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-200 animate-pulse" />}
                        <div>
                          <p className={`text-xs font-black ${passed ? 'text-slate-700' : 'text-slate-400'}`}>{item}</p>
                          <p className={`text-[9px] font-bold uppercase ${passed ? 'text-emerald-500' : 'text-slate-300'}`}>{passed ? 'Passed' : 'Pending'}</p>
                        </div>
                      </div>
                      {isEditMode &&
                        <button onClick={() => toggleCheck(item)} className={`px-3 py-1 rounded text-[10px] font-bold uppercase border transition-all ${passed ? 'text-slate-300 border-transparent hover:text-rose-500' : 'bg-white border-slate-200 text-slate-500 hover:border-indigo-500 hover:text-indigo-600 shadow-sm'}`}>
                          {passed ? 'Undo' : 'Mark Passed'}
                        </button>
                      }
                    </div>
                  );
                })}
              </div>
            </Section>

            {/* Receiving Form */}
            <Section title="Finished Goods Receiving" icon={<Package className="w-4 h-4 text-indigo-500" />}>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{"Warehouse"}</label>
                  <div className="relative flex items-center">
                    <input 
                      type={"text"} 
                      value={selectedData?.warehouseName ?? production?.warehouseName ?? "Main WH"} 
                      disabled
                      className={`w-full text-xs font-bold p-2.5 rounded-lg border-2 outline-none transition-all bg-slate-50 border-slate-50 text-slate-400 cursor-not-allowed`}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{"Prepared By"}</label>
                  <div className="relative flex items-center">
                    <input 
                      type={"text"} 
                      value={selectedData?.createdByFullName ?? fullName} 
                      disabled
                      className={`w-full text-xs font-bold p-2.5 rounded-lg border-2 outline-none transition-all bg-slate-50 border-slate-50 text-slate-400 cursor-not-allowed`}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{"Quantity Received"}<span className={"ml-1 text-red-500"}>*</span></label>
                  <div className="relative flex items-center">
                    <input 
                      type={"number"} 
                      value={quantityReceived} 
                      onChange={(e)=>setQuantityReceived(e.target.value)}
                      disabled={!isEditMode}
                      className={`w-full text-xs font-bold p-2.5 rounded-lg border-2 outline-none transition-all ${isEditMode ? 'bg-white border-slate-100 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 text-slate-700' : 'bg-slate-50 border-slate-50 text-slate-400 cursor-not-allowed'}`}
                    />
                    {production?.uomName && <span className="absolute right-2 px-1.5 py-0.5 bg-slate-100 text-[8px] font-black text-slate-500 rounded border border-slate-200 uppercase">{production?.uomName}</span>}
                  </div>
                </div>
              </div>
            </Section>

            {/* Remarks */}
            <Section title="Final Remarks" icon={<MessageSquare className="w-4 h-4 text-indigo-400" />}>
              <textarea 
                placeholder="Type any observations or adjustments here..."
                className="w-full border-2 border-slate-100 rounded-xl p-3 text-sm focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 outline-none h-24 transition-all"
                value={remarks}
                disabled={!isEditMode}
                onChange={(e)=>setRemarks(e.target.value)}
              />
            </Section>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-2 text-sm font-bold text-slate-500 hover:bg-slate-200 rounded-xl transition-colors">
            Cancel
          </button>
          { isEditMode &&
            <button 
              onClick={handleSubmit}
              className="flex items-center gap-2 px-8 py-2 text-sm font-black text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-lg shadow-indigo-200 transition-all active:scale-95"
            >
              <Check size={18} /> Complete Production
            </button>
          }
        </div>
      </div>
    </div>
  );
};

const ProductionSelect = ({ selectedData, setSelectedData, onClose, addHandleClick }) => {
  const [productions, setProductions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${domain}/api/Productions/actual-measurement-production-selection`);
      setProductions(response.data);
    } catch {
      toast.error("Failed to fetch productions.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter productions based on search term
  const filteredProductions = productions.filter(p => 
    p.productionNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.productName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[80vh] flex flex-col overflow-hidden border border-slate-200">
        
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="bg-indigo-600 p-2 rounded-lg shadow-lg shadow-indigo-100">
              <ClipboardList className="text-white w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-800 uppercase tracking-tight">Production Selection</h1>
              <p className="text-xs text-slate-400 font-medium">Select production to have actual measurement.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400">
            <X size={20} />
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder="Search by Production # or Product Name..." 
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table Content */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <Loader />
              <span className="text-sm text-slate-500 animate-pulse">Loading productions...</span>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="text-[11px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-200">
                  <th className="px-6 py-3">Production Details</th>
                  <th className="px-6 py-3">Warehouse</th>
                  <th className="px-6 py-3">Product Name</th>
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3 text-right">Actual</th>
                  <th className="px-6 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredProductions.length > 0 ? (
                  filteredProductions.map((item) => {
                    const isActive = selectedData?.id === item.id;
                    return (
                      <tr 
                        key={item.id}
                        onClick={() => setSelectedData(item)}
                        className={`group cursor-pointer transition-all hover:bg-indigo-50/30 ${isActive ? 'bg-indigo-50' : ''}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className={`text-sm font-bold ${isActive ? 'text-indigo-700' : 'text-slate-700'}`}>
                              {item.productionNumber}
                            </span>
                            <span className="text-[10px] text-slate-400">ID: {item.id}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-slate-600 font-medium">{item.warehouseName}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-sm text-slate-700 font-semibold">{item.productName}</span>
                            <span className="text-xs text-slate-400">{item.entries?.length || 0} items in combo</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {new Date(item.productionDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="text-sm font-mono font-bold text-slate-700">
                            {item.totalActualWeight} <small className="text-slate-400">{item.uomName}</small>
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {isActive ? (
                            <div className="inline-flex items-center justify-center text-indigo-600 bg-indigo-100 p-1 rounded-full">
                              <CheckCircle2 size={18} />
                            </div>
                          ) : (
                            <div className="inline-flex items-center justify-center text-slate-200 group-hover:text-slate-300">
                              <div className="w-5 h-5 rounded-full border-2 border-current" />
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-slate-400 italic">
                      No productions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
          <p className="text-xs text-slate-500">
            Showing {filteredProductions.length} productions
          </p>
          <button 
            onClick={()=>{addHandleClick(selectedData?.productionNumber ?? "")}}
            disabled={!selectedData}
            className={`px-6 py-2 bg-indigo-600 ${selectedData ? "hover:bg-indigo-700" : ""} text-white text-sm font-bold rounded-xl transition-all 
                      shadow-lg shadow-indigo-200 
                      disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed disabled:shadow-none disabled:border-slate-200 border border-transparent`}
          >
            Confirm Selection
          </button>
        </div>
      </div>
    </div>
  );
};

const SyncToAccountingModal = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onConfirm(dateFrom, dateTo);
    onClose();
  };
  
  const inputClass =
    "block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 transition-all focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 outline-none";
  const labelClass =
    "mb-1 ml-0.5 block text-[10px] font-black uppercase tracking-widest text-gray-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
        {/* Header */}
        <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
          <h2 className="text-white text-lg font-bold flex items-center gap-2">
            Sync To Accounting
          </h2>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-gray-600 mb-4">
            You are about to sync data to accounting.
            <br />
            <br />
            <strong>Please fill up fields to filter data</strong>
          </p>

          <div className="flex flex-col gap-3">
            <div>
              <label className={labelClass}>
                Order Date From
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className={`${inputClass} !border-indigo-300`}
              />
            </div>
            <div>
              <label className={labelClass}>
                Order Date To
                <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className={`${inputClass} !border-indigo-300`}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-md"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// --- HELPER COMPONENTS ---
const Section = ({ title, icon, children }) => (
  <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
    <div className="flex items-center gap-2 mb-4">
      <div className="p-1.5 bg-slate-50 rounded-md">{icon}</div>
      <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{title}</h2>
    </div>
    {children}
  </div>
);

export default ActualMeasurements;
